import jsPDF from 'jspdf';
import { Estimate, EstimateLineItem } from '../types';
import { PDFTemplateService } from './pdfTemplateService';
import { CompanyInfo } from './invoicePdfService';
import { calculateEstimateTotals, FeesForTotals } from '../utils/estimateTotals';
import { feesApi } from './api/feesApi';

export interface PDFLayoutOptions {
    layout: 'professional' | 'compact';
    includeCompanyLogo: boolean;
    colorScheme: 'blue' | 'green' | 'gray' | 'red' | 'purple' | 'orange';
    fontFamily: 'helvetica' | 'times' | 'courier';
    fontSize: 'small' | 'medium' | 'large';
    spacing: 'tight' | 'normal' | 'loose';
    borderStyle: 'none' | 'simple' | 'rounded';
    showPageNumbers: boolean;
}

export class ApprovalPDFService {
    private static readonly COLOR_SCHEMES = {
        blue: { primary: '#1890ff', secondary: '#f0f8ff', text: '#262626' },
        green: { primary: '#22c55e', secondary: '#f0fdf4', text: '#262626' },
        gray: { primary: '#595959', secondary: '#fafafa', text: '#262626' },
        red: { primary: '#ff4d4f', secondary: '#fff2f0', text: '#262626' },
        purple: { primary: '#722ed1', secondary: '#f9f0ff', text: '#262626' },
        orange: { primary: '#fa8c16', secondary: '#fff7e6', text: '#262626' },
    };

    private static readonly FONT_SETTINGS = {
        small: { title: 20, header: 12, body: 9, footer: 7 },
        medium: { title: 24, header: 14, body: 10, footer: 8 },
        large: { title: 28, header: 16, body: 12, footer: 9 },
    };

    private static readonly SPACING_SETTINGS = {
        tight: { lineHeight: 1.2, sectionGap: 8, itemGap: 5 },
        normal: { lineHeight: 1.4, sectionGap: 12, itemGap: 8 },
        loose: { lineHeight: 1.6, sectionGap: 16, itemGap: 10 },
    };

    static async generateApprovalPDF(
        estimate: Estimate,
        options?: Partial<PDFLayoutOptions>,
        companyInfo?: Partial<CompanyInfo>
    ): Promise<void> {
        const defaultOptions = { colorScheme: 'blue' as const };
        const finalOptions = PDFTemplateService.getSettingsWithOverrides({ ...defaultOptions, ...options });
        const finalCompanyInfo = PDFTemplateService.getCompanyInfoWithOverrides(companyInfo);
        // This is the document the customer SIGNS — its total must match what
        // the backend bills (fees + per-line taxability included), so fetch the
        // fee snapshots up front. Failing loudly beats signing a wrong number.
        const fees = await this.fetchFees(estimate);
        const pdf = await this.createPDF(estimate, finalOptions, finalCompanyInfo, fees);

        pdf.save(`estimate-approval-${estimate.estimate_number}.pdf`);
    }

    /** Sum the estimate's fee snapshots split by taxability (backend fee shape). */
    private static async fetchFees(estimate: Estimate): Promise<FeesForTotals> {
        const fees: FeesForTotals = { taxable: 0, nonTaxable: 0 };
        if (!estimate.id) return fees;
        const rows = await feesApi.list('estimates', estimate.id);
        for (const f of rows || []) {
            const amount = Number(
                f.computed_amount ?? (f.calculation_method === 'flat' ? f.value : 0)
            ) || 0;
            if (f.is_taxable) fees.taxable += amount;
            else fees.nonTaxable += amount;
        }
        return fees;
    }

    private static async createPDF(
        estimate: Estimate,
        options: PDFLayoutOptions,
        companyInfo: CompanyInfo,
        fees: FeesForTotals
    ): Promise<jsPDF> {
        const pdf = new jsPDF('p', 'mm', 'a4');
        const colors = this.COLOR_SCHEMES[options.colorScheme];

        pdf.setFont(options.fontFamily, 'normal');

        await this.generateLayout(pdf, estimate, options, companyInfo, colors, fees);

        return pdf;
    }

    private static formatCurrency(amount: number): string {
        return `$${(Math.round(amount * 100) / 100).toFixed(2)}`;
    }

    private static async generateLayout(
        pdf: jsPDF,
        estimate: Estimate,
        options: PDFLayoutOptions,
        companyInfo: CompanyInfo,
        colors: any,
        fees: FeesForTotals
    ): Promise<void> {
        const fonts = this.FONT_SETTINGS[options.fontSize];
        const spacing = this.SPACING_SETTINGS[options.spacing];
        const pageWidth = pdf.internal.pageSize.getWidth();
        let yPosition = 20;

        // Header
        yPosition = this.addHeader(pdf, companyInfo, options.includeCompanyLogo, colors, yPosition);

        // Title
        pdf.setFontSize(fonts.title);
        pdf.setTextColor(colors.primary);
        pdf.text('ESTIMATE APPROVAL', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += spacing.sectionGap + 7;

        // Estimate Details Box
        yPosition = this.addEstimateDetails(pdf, estimate, colors, yPosition, fonts, pageWidth);

        // Customer & Vehicle
        yPosition = this.addCustomerVehicleInfo(pdf, estimate, colors, yPosition, pageWidth, fonts);

        // Line Items with Status
        yPosition = this.addLineItemsTable(pdf, estimate, options, colors, yPosition, pageWidth, fonts);

        // Totals
        yPosition = this.addTotalSection(pdf, estimate, colors, yPosition, pageWidth, fees);

        // Signature Section
        if (estimate.signed_at && estimate.signature_data) {
            yPosition = this.addSignatureSection(pdf, estimate, colors, yPosition, pageWidth);
        }

        // Disclaimer
        if (estimate.disclaimer) {
            yPosition = this.addDisclaimer(pdf, estimate.disclaimer, yPosition, pageWidth);
        }

        // Footer
        this.addFooter(pdf, companyInfo, colors, options.showPageNumbers);
    }

    private static addHeader(
        pdf: jsPDF,
        companyInfo: CompanyInfo,
        includeLogo: boolean,
        colors: any,
        yPosition: number
    ): number {
        const pageWidth = pdf.internal.pageSize.getWidth();

        pdf.setFontSize(20);
        pdf.setTextColor(colors.primary);
        pdf.text(companyInfo.name, 20, yPosition);

        pdf.setFontSize(10);
        pdf.setTextColor(colors.text);
        yPosition += 8;
        pdf.text(companyInfo.address, 20, yPosition);
        yPosition += 4;
        pdf.text(`${companyInfo.city}, ${companyInfo.state} ${companyInfo.zip}`, 20, yPosition);
        yPosition += 4;
        pdf.text(`Phone: ${companyInfo.phone}`, 20, yPosition);
        yPosition += 4;
        pdf.text(`Email: ${companyInfo.email}`, 20, yPosition);

        if (companyInfo.website) {
            yPosition += 4;
            pdf.text(`Web: ${companyInfo.website}`, 20, yPosition);
        }

        if (includeLogo && companyInfo.logo) {
            try {
                pdf.addImage(companyInfo.logo, 'JPEG', pageWidth - 60, 20, 40, 30);
            } catch (error) {
                console.warn('Failed to add logo to PDF', error);
            }
        }

        return yPosition + 15;
    }

    private static addEstimateDetails(
        pdf: jsPDF,
        estimate: Estimate,
        colors: any,
        yPosition: number,
        fonts: any,
        pageWidth: number
    ): number {
        const boxHeight = 32;
        pdf.setDrawColor(colors.primary);
        pdf.setFillColor(colors.secondary);
        pdf.rect(20, yPosition, pageWidth - 40, boxHeight, 'F');
        pdf.rect(20, yPosition, pageWidth - 40, boxHeight);

        pdf.setFontSize(10);
        pdf.setTextColor(colors.text);

        const leftLabelX = 25;
        const leftValueX = 63;

        pdf.text('Estimate #:', leftLabelX, yPosition + 9);
        pdf.setFont(undefined, 'bold');
        pdf.text(estimate.estimate_number, leftValueX, yPosition + 9);

        pdf.setFont(undefined, 'normal');
        pdf.text('Date:', leftLabelX, yPosition + 17);
        const dateText = estimate.created_at ? new Date(estimate.created_at).toLocaleDateString() : 'N/A';
        pdf.text(dateText, leftValueX, yPosition + 17);

        // Right column
        const rightLabelX = pageWidth / 2 + 15;
        const rightValueX = pageWidth / 2 + 55;

        pdf.text('Status:', rightLabelX, yPosition + 9);
        pdf.setFont(undefined, 'bold');

        // Status Logic
        if (estimate.status === 'approved') {
            pdf.setTextColor(34, 197, 94);
            pdf.text('APPROVED', rightValueX, yPosition + 9);
        } else {
            pdf.setTextColor(colors.text);
            pdf.text(estimate.status.toUpperCase(), rightValueX, yPosition + 9);
        }

        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(colors.text);

        if (estimate.valid_until) {
            pdf.text('Valid Until:', rightLabelX, yPosition + 17);
            pdf.text(new Date(estimate.valid_until).toLocaleDateString(), rightValueX, yPosition + 17);
        }

        return yPosition + boxHeight + 10;
    }

    private static addCustomerVehicleInfo(
        pdf: jsPDF,
        estimate: Estimate,
        colors: any,
        yPosition: number,
        pageWidth: number,
        fonts: any
    ): number {
        const startYPosition = yPosition;

        // Customer info
        pdf.setFontSize(fonts.header);
        pdf.setTextColor(colors.primary);
        pdf.text('CUSTOMER', 20, yPosition);

        pdf.setFontSize(fonts.body);
        pdf.setTextColor(colors.text);
        yPosition += 8;

        if (estimate.customer) {
            const customerName = estimate.customer.company_name ||
                `${estimate.customer.first_name} ${estimate.customer.last_name}`;
            pdf.text(customerName, 20, yPosition);
            yPosition += 5;

            if (estimate.customer.address) {
                pdf.text(estimate.customer.address, 20, yPosition);
                yPosition += 5;
                pdf.text(`${estimate.customer.city}, ${estimate.customer.state} ${estimate.customer.zip}`, 20, yPosition);
                yPosition += 5;
            }

            pdf.text(`Phone: ${estimate.customer.phone}`, 20, yPosition);
            yPosition += 5;
            if (estimate.customer.email) {
                pdf.text(`Email: ${estimate.customer.email}`, 20, yPosition);
            }
        }

        // Vehicle info
        let vehicleYPosition = startYPosition;

        pdf.setFontSize(fonts.header);
        pdf.setTextColor(colors.primary);
        pdf.text('VEHICLE', pageWidth / 2 + 10, vehicleYPosition);

        pdf.setFontSize(fonts.body);
        pdf.setTextColor(colors.text);
        vehicleYPosition += 8;

        if (estimate.vehicle) {
            pdf.text(`${estimate.vehicle.year} ${estimate.vehicle.make} ${estimate.vehicle.model}`,
                pageWidth / 2 + 10, vehicleYPosition);
            vehicleYPosition += 5;

            if (estimate.vehicle.vin) {
                pdf.text(`VIN: ${estimate.vehicle.vin}`, pageWidth / 2 + 10, vehicleYPosition);
                vehicleYPosition += 5;
            }

            if (estimate.vehicle.license_plate) {
                pdf.text(`License: ${estimate.vehicle.license_plate}`, pageWidth / 2 + 10, vehicleYPosition);
                vehicleYPosition += 5;
            }

            if (estimate.vehicle.current_mileage) {
                pdf.text(`Mileage: ${estimate.vehicle.current_mileage.toLocaleString()}`,
                    pageWidth / 2 + 10, vehicleYPosition);
                vehicleYPosition += 5;
            }
        }

        return Math.max(yPosition, vehicleYPosition) + 10;
    }

    private static addLineItemsTable(
        pdf: jsPDF,
        estimate: Estimate,
        options: PDFLayoutOptions,
        colors: any,
        yPosition: number,
        pageWidth: number,
        fonts: any
    ): number {
        pdf.setFontSize(fonts.header);
        pdf.setTextColor(colors.primary);
        pdf.text('SERVICES & APPROVAL STATUS', 20, yPosition);
        yPosition += 15;

        const tableHeaders = ['Description', 'Status', 'Amount'];
        const colWidths = [110, 30, 40];
        const tableWidth = colWidths.reduce((sum, width) => sum + width, 0);
        const startX = 20;

        // Table header
        pdf.setFillColor(colors.primary);
        pdf.rect(startX, yPosition, tableWidth, 10, 'F');

        pdf.setFontSize(fonts.body);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont(undefined, 'bold');

        let xPos = startX + 3;
        tableHeaders.forEach((header, index) => {
            const textAlign = index === 0 ? 'left' : index === tableHeaders.length - 1 ? 'right' : 'center';
            const textX = textAlign === 'right' ? xPos + colWidths[index] - 3 :
                textAlign === 'center' ? xPos + colWidths[index] / 2 : xPos;
            pdf.text(header, textX, yPosition + 7, { align: textAlign });
            xPos += colWidths[index];
        });

        yPosition += 10;
        pdf.setFont(undefined, 'normal');

        // Rows
        estimate.line_items.forEach((item, index) => {
            // Simple page break check
            if (yPosition > pdf.internal.pageSize.getHeight() - 40) {
                pdf.addPage();
                yPosition = 20;
            }

            const rowHeight = 15;

            // Background
            if (index % 2 === 1) {
                pdf.setFillColor(250, 250, 250);
                pdf.rect(startX, yPosition, tableWidth, rowHeight, 'F');
            }

            // Description
            pdf.setFontSize(9);
            pdf.setTextColor(colors.text);
            pdf.text(item.description, startX + 3, yPosition + 6);
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`${item.category} - Qty: ${item.quantity}`, startX + 3, yPosition + 10);

            // Status
            const statusX = startX + colWidths[0] + colWidths[1] / 2;
            pdf.setFontSize(9);

            if (item.approval_status === 'approved') {
                pdf.setTextColor(34, 197, 94);
                pdf.text('APPROVED', statusX, yPosition + 9, { align: 'center' });
            } else if (item.approval_status === 'declined') {
                pdf.setTextColor(239, 68, 68);
                pdf.text('DECLINED', statusX, yPosition + 9, { align: 'center' });
            } else {
                pdf.setTextColor(156, 163, 175);
                pdf.text('PENDING', statusX, yPosition + 9, { align: 'center' });
            }

            // Amount
            const amountX = startX + colWidths[0] + colWidths[1] + colWidths[2] - 3;
            pdf.setTextColor(colors.text);
            pdf.text(this.formatCurrency(item.total_price), amountX, yPosition + 9, { align: 'right' });

            yPosition += rowHeight;
        });

        return yPosition + 10;
    }

    private static addTotalSection(
        pdf: jsPDF,
        estimate: Estimate,
        colors: any,
        yPosition: number,
        pageWidth: number,
        fees: FeesForTotals
    ): number {
        const rightAlign = pageWidth - 20;
        const labelX = rightAlign - 65;
        const valueX = rightAlign - 15;

        // Use shared totals helper — only include approved items
        const approvedStatus: Record<number, string> = {};
        for (const item of estimate.line_items) {
            if (item.id) {
                approvedStatus[item.id] = item.approval_status === 'approved' ? 'approved' : 'declined';
            }
        }
        // Whole-job (header) discount — applied after line discounts, before tax.
        const wholeJobDiscount = (estimate as any).discount_type && (estimate as any).discount_value != null
            ? { type: (estimate as any).discount_type, value: Number((estimate as any).discount_value) }
            : undefined;
        // Fees + per-line is_taxable flow through so the signed APPROVED TOTAL
        // matches the billed total (backend recalcEstimateTotals semantics).
        const totals = calculateEstimateTotals(estimate.line_items, estimate.tax_rate ?? 7, approvedStatus, wholeJobDiscount, fees);

        // Sanity: when every line is approved this must equal the stored,
        // billed total. Drift means a money bug — surface it for diagnosis.
        const allApproved = estimate.line_items.every(i => i.approval_status === 'approved');
        const storedTotal = Number((estimate as any).total_amount ?? NaN);
        if (allApproved && Number.isFinite(storedTotal) && Math.abs(totals.total - storedTotal) > 0.02) {
            console.warn('[approvalPdf] computed approved total differs from stored total', {
                computed: totals.total, stored: storedTotal, estimateId: estimate.id,
            });
        }

        pdf.setFontSize(10);
        pdf.setTextColor(colors.text);

        // Pre-discount subtotal
        pdf.text('Subtotal (before discounts):', labelX, yPosition, { align: 'right' });
        pdf.text(this.formatCurrency(totals.preDiscountSubtotal), valueX, yPosition, { align: 'right' });
        yPosition += 6;

        // Line-level discounts (if any)
        if (totals.totalDiscounts > 0) {
            pdf.setTextColor(239, 68, 68);
            pdf.text('Discounts:', labelX, yPosition, { align: 'right' });
            pdf.text(`-${this.formatCurrency(totals.totalDiscounts)}`, valueX, yPosition, { align: 'right' });
            yPosition += 6;

            pdf.setTextColor(colors.text);
            pdf.text('Subtotal:', labelX, yPosition, { align: 'right' });
            pdf.text(this.formatCurrency(totals.discountedSubtotal), valueX, yPosition, { align: 'right' });
            yPosition += 6;
        }

        // Whole-job discount (if any) — show it so Subtotal − Discount + Tax = Total reconciles
        if (totals.jobDiscountAmount > 0) {
            pdf.setTextColor(239, 68, 68);
            pdf.text('Job Discount:', labelX, yPosition, { align: 'right' });
            pdf.text(`-${this.formatCurrency(totals.jobDiscountAmount)}`, valueX, yPosition, { align: 'right' });
            yPosition += 6;

            pdf.setTextColor(colors.text);
            pdf.text('Discounted Subtotal:', labelX, yPosition, { align: 'right' });
            pdf.text(this.formatCurrency(totals.taxableSubtotal), valueX, yPosition, { align: 'right' });
            yPosition += 6;
        }

        // Fees & surcharges (join the total; taxable fees join the tax base)
        if (totals.feesTotal > 0) {
            pdf.setTextColor(colors.text);
            pdf.text('Fees & Surcharges:', labelX, yPosition, { align: 'right' });
            pdf.text(this.formatCurrency(totals.feesTotal), valueX, yPosition, { align: 'right' });
            yPosition += 6;
        }

        // Tax
        pdf.setTextColor(colors.text);
        pdf.text(`Tax (${totals.taxRate.toFixed(2)}%):`, labelX, yPosition, { align: 'right' });
        pdf.text(this.formatCurrency(totals.taxAmount), valueX, yPosition, { align: 'right' });
        yPosition += 6;

        // Total
        pdf.setFontSize(14);
        pdf.setTextColor(colors.primary);
        pdf.text('APPROVED TOTAL:', labelX, yPosition, { align: 'right' });
        pdf.setFont(undefined, 'bold');
        pdf.text(this.formatCurrency(totals.total), valueX, yPosition, { align: 'right' });
        pdf.setFont(undefined, 'normal');

        return yPosition + 20;
    }

    private static addSignatureSection(
        pdf: jsPDF,
        estimate: Estimate,
        colors: any,
        yPosition: number,
        pageWidth: number
    ): number {
        const boxWidth = pageWidth - 40;

        const disclaimerText = `Authorization:
I hereby authorize the repair work described above to be performed, along with the provision of necessary materials. You and your employees are granted permission to operate the vehicle described herein on streets, highways, or elsewhere for the purposes of testing and/or inspection.

Payment & Vehicle Removal Policy:
Full payment or a formal payment arrangement must be honored and established within 3 business days of repair completion. Additionally, the vehicle must be removed from the premises within 3 business days of notification that repairs are complete. Failure to comply with these terms may result in additional storage fees.`;

        // Calculate text height to determine box size
        pdf.setFontSize(9);
        const textLines = pdf.splitTextToSize(disclaimerText, boxWidth - 10);
        const textHeight = textLines.length * 4; // Approx 4mm per line

        const signatureHeight = 40; // Height reserved for signature area
        const boxHeight = textHeight + signatureHeight + 15; // Total box height with padding

        // Check for page break
        if (yPosition + boxHeight > pdf.internal.pageSize.getHeight()) {
            pdf.addPage();
            yPosition = 20;
        }

        // Draw Box
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.rect(20, yPosition, boxWidth, boxHeight);

        // Draw Text
        pdf.setTextColor(colors.text);
        pdf.text(textLines, 25, yPosition + 8);

        // Calculate Y position for signature (below text)
        const signatureY = yPosition + textHeight + 10;

        // Signature Image
        if (estimate.signature_data) {
            try {
                pdf.addImage(estimate.signature_data, 'PNG', 30, signatureY, 60, 20);
            } catch (e) {
                console.warn('Failed to add signature image', e);
            }
        }

        // Line for signature
        const lineY = signatureY + 20;
        pdf.line(30, lineY, 120, lineY);
        pdf.setFontSize(8);
        pdf.text('Customer Signature', 30, lineY + 4);

        // Date
        if (estimate.signed_at) {
            const dateStr = new Date(estimate.signed_at).toLocaleString();
            pdf.text(`Date: ${dateStr}`, 130, lineY);

            const sigTypeLabel = estimate.signature_type === 'in_person'
                ? 'Signed in person'
                : 'Signed remotely';
            pdf.setFontSize(8);
            pdf.setTextColor(100, 100, 100);
            pdf.text(sigTypeLabel, 130, lineY + 5);
        }

        return yPosition + boxHeight + 20;
    }

    private static addDisclaimer(
        pdf: jsPDF,
        disclaimer: string,
        yPosition: number,
        pageWidth: number
    ): number {
        if (yPosition + 30 > pdf.internal.pageSize.getHeight()) {
            pdf.addPage();
            yPosition = 20;
        }

        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        pdf.text(disclaimer, 20, yPosition, { maxWidth: pageWidth - 40 });

        return yPosition + 20;
    }

    private static addFooter(
        pdf: jsPDF,
        companyInfo: CompanyInfo,
        colors: any,
        showPageNumbers: boolean
    ) {
        const pageCount = pdf.internal.getNumberOfPages();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8);
            pdf.setTextColor(150, 150, 150);

            // Footer line
            pdf.setDrawColor(220, 220, 220);
            pdf.line(20, pageHeight - 15, pageWidth - 20, pageHeight - 15);

            // Company info
            const footerText = `${companyInfo.name} | ${companyInfo.phone} | ${companyInfo.email}`;
            pdf.text(footerText, 20, pageHeight - 10);

            // Page number
            if (showPageNumbers) {
                pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
            }
        }
    }
}
